import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useCurrencyStore from '../store/useCurrencyStore';
import api from '../services/api';
import useToastStore from '../store/useToastStore';

const CATEGORIES = [
  { value: 'MODELS', label: 'Models' },
  { value: 'VOICEOVER', label: 'Voiceover' },
  { value: 'DESIGN_BRANDING', label: 'Design & Branding' },
  { value: 'MARKETING_SOCIAL', label: 'Marketing & Social' },
  { value: 'VIDEO_PHOTOGRAPHY', label: 'Video & Photography' },
  { value: 'WEB_DEVELOPMENT', label: 'Web & Development' },
  { value: 'CONTENT_CREATION', label: 'Content Creation' },
  { value: 'STUDIO_RENTALS', label: 'Studio Rentals' },
  { value: 'EVENT_PLANNING', label: 'Event Planning' },
];

const MODEL_TYPES = [
  { value: 'FACE', label: 'Face Shown' },
  { value: 'NO_FACE', label: 'No Face (Body Only)' },
];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_VALUES = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const EMPTY_FORM = {
  title: '',
  description: '',
  category: '',
  listingType: 'SERVICE',
  price: '',
  deliveryDays: '',
  hourlyRate: '',
  address: '',
  tags: '',
  capacity: '',
  equipment: '',
  amenities: '',
  availableDays: [],
  availableFrom: '',
  availableTo: '',
  modelType: '',
};

const isVideo = (src) => {
  if (!src) return false;
  if (src instanceof File) return src.type.startsWith('video/');
  return /\.(mp4|mov|avi|mkv|webm)$/i.test(src) || src.includes('/video/');
};

export default function CreateServicePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currency } = useCurrencyStore();
  const currLabel = currency?.code || 'KWD';
  const isEditing = Boolean(id);

  const [form, setForm] = useState(EMPTY_FORM);
  const [images, setImages] = useState([]); // array of File objects
  const [existingImages, setExistingImages] = useState([]); // URLs from server
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loadingService, setLoadingService] = useState(isEditing);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isEditing) return;
    const fetchService = async () => {
      try {
        const { data } = await api.get(`/services/${id}`);
        setForm({
          title: data.title || '',
          description: data.description || '',
          category: data.category || '',
          listingType: data.listingType || 'SERVICE',
          price: data.price ? Math.round(data.price * (currency?.rate || 1)) : '',
          deliveryDays: data.deliveryDays ?? '',
          hourlyRate: data.hourlyRate ? Math.round(data.hourlyRate * (currency?.rate || 1)) : '',
          address: data.address || '',
          tags: Array.isArray(data.tags) ? data.tags.filter(t => t !== 'FACE' && t !== 'NO_FACE').join(', ') : (data.tags || ''),
          modelType: Array.isArray(data.tags) ? (data.tags.find(t => t === 'FACE' || t === 'NO_FACE') || '') : '',
          capacity: data.capacity ?? '',
          equipment: Array.isArray(data.equipment) ? data.equipment.join(', ') : (data.equipment || ''),
          amenities: Array.isArray(data.amenities) ? data.amenities.join(', ') : (data.amenities || ''),
          availableDays: Array.isArray(data.availableDays) ? data.availableDays : [],
          availableFrom: data.availableFrom || '',
          availableTo: data.availableTo || '',
        });
        setExistingImages(data.images || []);
      } catch {
        setError('Could not load service.');
      }
      setLoadingService(false);
    };
    fetchService();
  }, [id, isEditing]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleImageSlotTap = (index) => {
    if (index < existingImages.length + images.length) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const totalSlots = existingImages.length + images.length;
    const remaining = 5 - totalSlots;
    setImages((prev) => [...prev, ...files.slice(0, remaining)]);
    e.target.value = '';
  };

  const removeExistingImage = (idx) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const removeNewImage = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    if (!form.category) { setError('Please select a category.'); return; }
    setError('');
    setSubmitting(true);

    try {
      const fd = new FormData();
      fd.append('title', form.title.trim());
      fd.append('description', form.description.trim());
      fd.append('category', form.category);
      fd.append('listingType', form.listingType);
      // Convert price from user's currency to AED for storage
      const rate = currency?.rate || 1;
      if (form.listingType === 'SERVICE' && form.price !== '') fd.append('price', parseFloat(form.price) / rate);
      if (form.listingType === 'SERVICE' && form.deliveryDays !== '') fd.append('deliveryDays', form.deliveryDays);
      if (form.listingType === 'STUDIO' && form.hourlyRate !== '') fd.append('hourlyRate', parseFloat(form.hourlyRate) / rate);
      if (form.address.trim()) fd.append('address', form.address.trim());
      const allTags = [form.tags.trim(), form.category === 'MODELS' && form.modelType ? form.modelType : ''].filter(Boolean).join(', ');
      if (allTags) fd.append('tags', allTags);
      if (form.availableDays.length > 0) fd.append('availableDays', form.availableDays.join(','));
      if (form.availableFrom) fd.append('availableFrom', form.availableFrom);
      if (form.availableTo) fd.append('availableTo', form.availableTo);
      if (form.listingType === 'STUDIO') {
        if (form.capacity !== '') fd.append('capacity', form.capacity);
        if (form.equipment.trim()) fd.append('equipment', form.equipment.trim());
        if (form.amenities.trim()) fd.append('amenities', form.amenities.trim());
      }
      if (isEditing) {
        fd.append('existingImages', JSON.stringify(existingImages));
      }
      images.forEach((file) => fd.append('images', file));

      if (isEditing) {
        await api.put(`/services/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/services', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      navigate('/my-services');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  const totalImages = existingImages.length + images.length;
  const slots = Array.from({ length: 5 });

  if (loadingService) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand-purple border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg pb-24">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Header */}
      <div className="px-4 pt-12 pb-4 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-xl bg-dark-surface border border-dark-border"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-dark-text">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h1 className="text-xl font-bold text-dark-text" style={{ letterSpacing: '-0.5px' }}>
          {isEditing ? 'Edit Listing' : 'New Listing'}
        </h1>
      </div>

      <div className="px-4 flex flex-col gap-5">

        {/* Image upload row */}
        <div>
          <p className="text-dark-subtext text-xs font-medium mb-2 uppercase tracking-wide">Photos & Videos (up to 5)</p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {slots.map((_, i) => {
              const isExisting = i < existingImages.length;
              const isNew = !isExisting && i < existingImages.length + images.length;
              const newImgIdx = i - existingImages.length;
              const isFilled = isExisting || isNew;
              const preview = isExisting
                ? existingImages[i]
                : isNew
                ? URL.createObjectURL(images[newImgIdx])
                : null;

              return (
                <div
                  key={i}
                  onClick={() => !isFilled && handleImageSlotTap(i)}
                  className={`relative flex-shrink-0 w-20 h-20 rounded-2xl border-2 overflow-hidden flex items-center justify-center transition-all ${
                    isFilled
                      ? 'border-brand-purple'
                      : 'border-dashed border-dark-border bg-dark-surface cursor-pointer'
                  }`}
                >
                  {preview ? (
                    <>
                      {isVideo(isNew ? images[newImgIdx] : preview) ? (
                        <div className="w-full h-full bg-dark-bg flex items-center justify-center">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6C5CE7" strokeWidth="2">
                            <polygon points="5 3 19 12 5 21 5 3"/>
                          </svg>
                        </div>
                      ) : (
                        <img src={preview} className="w-full h-full object-cover" alt="" />
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          isExisting ? removeExistingImage(i) : removeNewImage(newImgIdx);
                        }}
                        className="absolute top-1 right-1 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center"
                      >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </>
                  ) : (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6B6B7A" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="3" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                  )}
                </div>
              );
            })}
          </div>
          {totalImages < 5 && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="mt-2 text-brand-purple text-xs font-medium"
            >
              + Add photo
            </button>
          )}
        </div>

        {/* Title */}
        <div>
          <label className="text-dark-subtext text-xs font-medium mb-1.5 block uppercase tracking-wide">Title *</label>
          <input
            value={form.title}
            onChange={set('title')}
            placeholder="e.g. Professional Logo Design"
            className="w-full bg-dark-surface border border-dark-border rounded-xl px-4 py-3 text-dark-text text-sm outline-none focus:border-brand-purple placeholder:text-dark-muted"
          />
        </div>

        {/* Description */}
        <div>
          <label className="text-dark-subtext text-xs font-medium mb-1.5 block uppercase tracking-wide">Description</label>
          <textarea
            value={form.description}
            onChange={set('description')}
            rows={4}
            placeholder="Describe what you offer, your process, and what clients can expect..."
            className="w-full bg-dark-surface border border-dark-border rounded-xl px-4 py-3 text-dark-text text-sm outline-none focus:border-brand-purple placeholder:text-dark-muted resize-none"
          />
        </div>

        {/* Category */}
        <div>
          <label className="text-dark-subtext text-xs font-medium mb-1.5 block uppercase tracking-wide">Category *</label>
          <div className="relative">
            <select
              value={form.category}
              onChange={set('category')}
              className="w-full bg-dark-surface border border-dark-border rounded-xl px-4 py-3 text-dark-text text-sm outline-none focus:border-brand-purple appearance-none"
            >
              <option value="">Select a category...</option>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <svg className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B6B7A" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </div>

        {/* Model type (only for MODELS category) */}
        {form.category === 'MODELS' && (
          <div>
            <label className="text-dark-subtext text-xs font-medium mb-1.5 block uppercase tracking-wide">Model Type</label>
            <div className="flex bg-dark-surface border border-dark-border rounded-xl p-1">
              {MODEL_TYPES.map((mt) => (
                <button
                  key={mt.value}
                  onClick={() => setForm((f) => ({ ...f, modelType: mt.value }))}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                    form.modelType === mt.value ? 'bg-brand-gradient text-white' : 'text-dark-subtext'
                  }`}
                >
                  {mt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Listing type toggle */}
        <div>
          <label className="text-dark-subtext text-xs font-medium mb-1.5 block uppercase tracking-wide">Listing Type</label>
          <div className="flex bg-dark-surface border border-dark-border rounded-xl p-1">
            <button
              onClick={() => setForm((f) => ({ ...f, listingType: 'SERVICE' }))}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                form.listingType === 'SERVICE' ? 'bg-brand-gradient text-white' : 'text-dark-subtext'
              }`}
            >
              Service
            </button>
            <button
              onClick={() => setForm((f) => ({ ...f, listingType: 'STUDIO' }))}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                form.listingType === 'STUDIO' ? 'bg-brand-gradient text-white' : 'text-dark-subtext'
              }`}
            >
              Studio
            </button>
          </div>
        </div>

        {/* SERVICE fields */}
        {form.listingType === 'SERVICE' && (
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-dark-subtext text-xs font-medium mb-1.5 block uppercase tracking-wide">Price ({currLabel})</label>
              <input
                type="number"
                value={form.price}
                onChange={set('price')}
                placeholder="0"
                min="0"
                className="w-full bg-dark-surface border border-dark-border rounded-xl px-4 py-3 text-dark-text text-sm outline-none focus:border-brand-purple placeholder:text-dark-muted"
              />
            </div>
            <div className="flex-1">
              <label className="text-dark-subtext text-xs font-medium mb-1.5 block uppercase tracking-wide">Delivery (days)</label>
              <input
                type="number"
                value={form.deliveryDays}
                onChange={set('deliveryDays')}
                placeholder="e.g. 3"
                min="1"
                className="w-full bg-dark-surface border border-dark-border rounded-xl px-4 py-3 text-dark-text text-sm outline-none focus:border-brand-purple placeholder:text-dark-muted"
              />
            </div>
          </div>
        )}

        {/* STUDIO fields */}
        {form.listingType === 'STUDIO' && (
          <>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-dark-subtext text-xs font-medium mb-1.5 block uppercase tracking-wide">Hourly Rate ({currLabel})</label>
                <input
                  type="number"
                  value={form.hourlyRate}
                  onChange={set('hourlyRate')}
                  placeholder="0"
                  min="0"
                  className="w-full bg-dark-surface border border-dark-border rounded-xl px-4 py-3 text-dark-text text-sm outline-none focus:border-brand-purple placeholder:text-dark-muted"
                />
              </div>
              <div className="flex-1">
                <label className="text-dark-subtext text-xs font-medium mb-1.5 block uppercase tracking-wide">Capacity (pax)</label>
                <input
                  type="number"
                  value={form.capacity}
                  onChange={set('capacity')}
                  placeholder="e.g. 10"
                  min="1"
                  className="w-full bg-dark-surface border border-dark-border rounded-xl px-4 py-3 text-dark-text text-sm outline-none focus:border-brand-purple placeholder:text-dark-muted"
                />
              </div>
            </div>
            <div>
              <label className="text-dark-subtext text-xs font-medium mb-1.5 block uppercase tracking-wide">Equipment</label>
              <input
                value={form.equipment}
                onChange={set('equipment')}
                placeholder="e.g. Camera, Lighting, Green Screen (comma-separated)"
                className="w-full bg-dark-surface border border-dark-border rounded-xl px-4 py-3 text-dark-text text-sm outline-none focus:border-brand-purple placeholder:text-dark-muted"
              />
            </div>
            <div>
              <label className="text-dark-subtext text-xs font-medium mb-1.5 block uppercase tracking-wide">Amenities</label>
              <input
                value={form.amenities}
                onChange={set('amenities')}
                placeholder="e.g. WiFi, Parking, AC (comma-separated)"
                className="w-full bg-dark-surface border border-dark-border rounded-xl px-4 py-3 text-dark-text text-sm outline-none focus:border-brand-purple placeholder:text-dark-muted"
              />
            </div>
          </>
        )}

        {/* Address */}
        <div>
          <label className="text-dark-subtext text-xs font-medium mb-1.5 block uppercase tracking-wide">Address / Location</label>
          <input
            value={form.address}
            onChange={set('address')}
            placeholder="e.g. Salmiya, Kuwait City"
            className="w-full bg-dark-surface border border-dark-border rounded-xl px-4 py-3 text-dark-text text-sm outline-none focus:border-brand-purple placeholder:text-dark-muted"
          />
        </div>

        {/* Availability */}
        <div>
          <label className="text-dark-subtext text-xs font-medium mb-2 block uppercase tracking-wide">Availability (optional)</label>
          <div className="flex gap-1.5 flex-wrap mb-3">
            {DAYS.map((day, i) => {
              const val = DAY_VALUES[i];
              const active = form.availableDays.includes(val);
              return (
                <button
                  key={val}
                  type="button"
                  onClick={() => setForm(f => ({
                    ...f,
                    availableDays: active ? f.availableDays.filter(d => d !== val) : [...f.availableDays, val]
                  }))}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    active
                      ? 'bg-brand-purple border-brand-purple text-white'
                      : 'border-dark-border text-dark-subtext'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
          {form.availableDays.length > 0 && (
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-dark-subtext text-xs mb-1 block">From</label>
                <input
                  type="time"
                  value={form.availableFrom}
                  onChange={set('availableFrom')}
                  className="w-full bg-dark-surface border border-dark-border rounded-xl px-4 py-2.5 text-dark-text text-sm outline-none focus:border-brand-purple"
                  style={{ colorScheme: 'dark' }}
                />
              </div>
              <div className="flex-1">
                <label className="text-dark-subtext text-xs mb-1 block">To</label>
                <input
                  type="time"
                  value={form.availableTo}
                  onChange={set('availableTo')}
                  className="w-full bg-dark-surface border border-dark-border rounded-xl px-4 py-2.5 text-dark-text text-sm outline-none focus:border-brand-purple"
                  style={{ colorScheme: 'dark' }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Tags */}
        <div>
          <label className="text-dark-subtext text-xs font-medium mb-1.5 block uppercase tracking-wide">Tags</label>
          <input
            value={form.tags}
            onChange={set('tags')}
            placeholder="e.g. logo, branding, identity (comma-separated)"
            className="w-full bg-dark-surface border border-dark-border rounded-xl px-4 py-3 text-dark-text text-sm outline-none focus:border-brand-purple placeholder:text-dark-muted"
          />
          <p className="text-dark-subtext text-xs mt-1">Separate tags with commas</p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full py-4 rounded-2xl bg-brand-gradient text-white font-bold text-base disabled:opacity-60 transition-opacity"
        >
          {submitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Publish Listing'}
        </button>

      </div>
    </div>
  );
}
